import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogView, Button } from '../index'
import { enqueueWarehouseServiceSummaryPreviewExport, getWarehouseServiceSummaryPreviewExport } from '../../services'

export const BillingExportPreviewModal = ({ 
    open, 
    setOpen, 
    warehouseId, 
    onSuccess = () => {},
    onError = () => {}
}) => {
    const { t, i18n } = useTranslation()
    const [isExporting, setIsExporting] = useState(false)
    const [progress, setProgress] = useState(0)
    const [isCompleted, setIsCompleted] = useState(false)
    const [error, setError] = useState(null)
    const [jobId, setJobId] = useState(null)
    const [downloadUrl, setDownloadUrl] = useState(null)
    const intervalRef = useRef(null)

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [])

    // Reset state when modal opens/closes
    useEffect(() => {
        if (open) {
            resetState()
            handleStartExport()
        } else {
            clearInterval(intervalRef.current)
        }
    }, [open])

    const resetState = () => {
        setIsExporting(false)
        setProgress(0)
        setIsCompleted(false)
        setError(null)
        setJobId(null)
        setDownloadUrl(null)
        clearInterval(intervalRef.current)
    }

    const handleStartExport = async () => {
        setIsExporting(true)
        setError(null)

        try {
            const response = await enqueueWarehouseServiceSummaryPreviewExport(warehouseId)
            
            // Check if it's a concurrency conflict
            if (response.message && response.message.includes('already in progress')) {
                setError(t('billing.stores.export_already_in_progress'))
                setIsExporting(false)
                return
            }

            // Start progress tracking
            if (response.job_id) {
                setJobId(response.job_id)
                startProgressTracking(response.job_id)
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('billing.stores.export_error'))
            setIsExporting(false)
            onError(err)
        }
    }

    const startProgressTracking = (jobId) => {
        intervalRef.current = setInterval(async () => {
            try {
                const { progress_percentage: progressPercentage, download_url: downloadUrl } = await getWarehouseServiceSummaryPreviewExport(jobId)
                
                setProgress(progressPercentage || 0)
                
                if (progressPercentage >= 100 && downloadUrl) {
                    setDownloadUrl(downloadUrl)
                    setIsCompleted(true)
                    setIsExporting(false)
                    clearInterval(intervalRef.current)
                    
                    // Call success callback after a short delay
                    setTimeout(() => {
                        onSuccess()
                    }, 2000)
                }
            } catch (err) {
                setError(err.response?.data?.error || err.message || t('billing.stores.export_progress_error'))
                setIsExporting(false)
                clearInterval(intervalRef.current)
                onError(err)
            }
        }, 1000) // Poll every 1 second
    }

    const handleClose = () => {
        if (isExporting) return // Prevent closing during export
        setOpen(false)
    }

    const handleDownload = () => {
        if (downloadUrl) {
            window.open(process.env.REACT_APP_PLATFORM_URL + downloadUrl)
        }
        setOpen(false)
    }

    return (
        <DialogView open={open} setOpen={handleClose}>
            <div className="w-full">
                
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        {t('billing.stores.download_preview')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('billing.stores.export_description')}
                    </p>
                </div>

                <div className="px-6 py-4">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {isExporting && !isCompleted && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <h4 className="text-lg font-medium text-gray-900 mb-2">
                                    {t('billing.stores.export_generating')}
                                </h4>
                                
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2 mt-4">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                                
                                <p className="text-sm text-gray-600">
                                    {progress}% {t('generic.completed')}
                                </p>
                            </div>
                        </div>
                    )}

                    {isCompleted && (
                        <div className="text-center py-4">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">
                                {t('billing.stores.export_completed')}
                            </h4>
                            <p className="text-sm text-gray-600">
                                {t('billing.stores.export_success_message')}
                            </p>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 flex justify-end space-x-3">
                    {isCompleted && (
                        <Button
                            type="primary"
                            onClick={handleDownload}
                        >
                            {i18n.t('list_locations.download')}
                        </Button>
                    )}
                    
                    {!isExporting && !isCompleted && error && (
                        <Button
                            type="primary"
                            onClick={() => setOpen(false)}
                        >
                            {t('generic.close')}
                        </Button>
                    )}
                </div>
            </div>
        </DialogView>
    )
}
